import { useEffect, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type { OwlTier } from "./useOwlHourSlips.ts";

export interface OwlPublicationState {
  tier: OwlTier; session_date: string; refresh_phase: string; previous_refresh: boolean;
  state: "FULL" | "REDUCED" | "RECOVERY" | "ASSEMBLING" | "DELAYED" | "NO_PUBLICATION";
}
export const OWL_PUBLICATION_LABELS: Record<OwlPublicationState["state"], string> = {
  FULL: "Published", REDUCED: "Reduced board", RECOVERY: "Recovery in progress",
  ASSEMBLING: "Assembling", DELAYED: "Publication delayed", NO_PUBLICATION: "No publication",
};
export function owlPublicationLabel(entry: Pick<OwlPublicationState, "state" | "previous_refresh">): string {
  return entry.previous_refresh ? "Previous refresh" : OWL_PUBLICATION_LABELS[entry.state] ?? "No publication";
}

export function useOwlPublicationStates(): readonly OwlPublicationState[] {
  const { user } = useAuthSession();
  const [states, setStates] = useState<readonly OwlPublicationState[]>([]);
  useEffect(() => {
    setStates([]);
    if (!user) return;
    let cancelled = false;
    const read = async () => {
      try {
        const { data, error } = await (getBrowserClient().rpc as any)("get_owl_publication_states");
        if (!cancelled && !error && Array.isArray(data)) setStates(data);
      } catch { /* The publication hook owns the visible connectivity error. */ }
    };
    void read();
    const timer = window.setInterval(() => { void read(); }, 60000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [user?.id]);
  return states;
}
